import { Injectable } from '@angular/core';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { selectRouteParams } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import {
  extractGuidFromString,
  isEqual,
  isNonNullish,
} from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  RouterNavigationAction,
  ROUTER_NAVIGATION,
  ROUTER_REQUEST,
} from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';

import { setOpenedList, trackList } from '../actions';

const trackPaths = [
  APP_PRIVATE_PATHS.productList.path,
  APP_PRIVATE_PATHS.browse.path,
  APP_PRIVATE_PATHS.home.path,
  APP_PRIVATE_PATHS.shopTheLook.path,
];

@Injectable()
export class RouteEffects {
  private routerNavigationStream$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATION),
    filter(
      ({
        payload: {
          event: { url },
        },
      }: RouterNavigationAction) =>
        trackPaths.some((path) => url.includes(path)),
    ),
    switchMap(() => this.store.pipe(select(selectRouteParams), take(1))),
    switchMap((routerParams) =>
      this.store.pipe(
        select(selectSiteId),
        filter(Boolean),
        take(1),
        map(() => routerParams),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public setOpenedState$ = createEffect(() =>
    combineLatest([
      this.store.pipe(select(selectAccountId), filter(isNonNullish)),
      this.store.pipe(select(selectSiteId), filter(isNonNullish)),
      this.store.pipe(select(selectSubSiteId)),
      this.routerNavigationStream$,
    ]).pipe(
      map(([accountId, siteId, subSiteId, ids]) => [
        accountId,
        siteId,
        subSiteId,
        'listId' in ids ? ids.listId : ids.id,
      ]),
      distinctUntilChanged(isEqual), // be careful in removing as this is used in RTE-1741, RTE-1716, and FRT-350
      map(([, , , id]) => id),
      filter((id) => isNonNullish(id)),
      map((id) => setOpenedList({ id })),
    ),
  );

  public trackListFromUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_REQUEST),
      filter(({ payload: { event } }: RouterNavigationAction) => {
        // tslint:disable-next-line:no-commented-code
        // const firstUrl = routerState.url.length === 0;
        // const fromSiteSelector =
        //   routerState.url.indexOf(APP_TOP_LEVEL_PATH.siteSelector.path) > -1;
        // const fromAccountSelector =
        //   routerState.url.indexOf(APP_TOP_LEVEL_PATH.accountSelector.path) > -1;

        // as we don't use source property in payload now adn just use track for get users who viewed the list,
        // we can dispatch trackList every time User goes to the list page to be sure UI doesn't miss any event
        // if we start use "source" - uncomment commented js code
        return event.url.includes(APP_PRIVATE_PATHS.productList.path);
        // && (firstUrl || fromAccountSelector || fromSiteSelector)
      }),
      map(({ payload: { event } }) => extractGuidFromString(event.url)?.[0]),
      filter(isNonNullish),
      distinctUntilChanged(),
      map((productListId) =>
        trackList({
          payload: {
            productListId,
            source: 'url',
          },
        }),
      ),
    ),
  );

  constructor(private store: Store, private actions$: Actions) {}
}
